import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import PickupMap from "../../components/PickupMap";

const card = "rounded-2xl border border-adminborder bg-admincard";
const TASK_BADGE = {
  scheduled: "bg-sky-500/15 text-sky-400", reached: "bg-violet-500/15 text-violet-400",
  pickup_done: "bg-emerald-500/15 text-emerald-400", bill_shared: "bg-amber-500/15 text-amber-400",
  amount_taken: "bg-teal-500/15 text-teal-400", submitted_office: "bg-slate-500/15 text-slate-400",
};
const LEAD_BADGE = { new: "bg-sky-500/15 text-sky-400", followup: "bg-amber-500/15 text-amber-400", agreed: "bg-emerald-500/15 text-emerald-400" };
const REQ_BADGE = { new: "bg-sky-500/15 text-sky-400", assigned: "bg-emerald-500/15 text-emerald-400", cancelled: "bg-slate-500/15 text-slate-400" };

export default function AdminCrm() {
  const [leads, setLeads] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [mapPoints, setMapPoints] = useState([]);
  const [boys, setBoys] = useState([]);
  const [openLead, setOpenLead] = useState(null);
  const [requests, setRequests] = useState([]);
  const [assignSel, setAssignSel] = useState({});
  const [busyId, setBusyId] = useState("");

  const loadRequests = useCallback(() => {
    api.get("/crm/pickup-requests").then((r) => setRequests(r.data)).catch(() => {});
  }, []);

  const assignRequest = async (req) => {
    const boyId = assignSel[req.id];
    if (!boyId) { toast.error("Select a pickup boy first"); return; }
    setBusyId(req.id);
    try {
      const res = await api.post(`/crm/pickup-requests/${req.id}/assign`, { pickup_boy_id: boyId });
      toast.success(`Assigned to ${boys.find((b) => b.id === boyId)?.name || "pickup partner"}`);
      if (res.data?.notify?.wa_link) {
        toast.success("Send pickup confirmation to customer", { duration: 12000,
          action: { label: "WhatsApp", onClick: () => notifyCustomer(res.data) } });
      }
      loadRequests();
      api.get("/crm/pickups").then((r) => setPickups(r.data)).catch(() => {});
    } catch (err) { toast.error(err?.response?.data?.detail || "Assign failed"); }
    finally { setBusyId(""); }
  };

  const notifyCustomer = async (t) => {
    const link = t.notify?.wa_link;
    if (!link) { toast.error("Customer phone unavailable for WhatsApp"); return; }
    window.open(link, "_blank");
    try { await api.post(`/crm/tasks/${t.id}/notify-sent`); api.get("/crm/pickups").then((r) => setPickups(r.data)).catch(() => {}); } catch { /* noop */ }
  };

  const cancelRequest = async (req) => {
    setBusyId(req.id);
    try {
      await api.post(`/crm/pickup-requests/${req.id}/cancel`);
      toast.success("Request cancelled");
      loadRequests();
    } catch (err) { toast.error(err?.response?.data?.detail || "Cancel failed"); }
    finally { setBusyId(""); }
  };

  const loadMap = useCallback(() => api.get("/crm/map").then((r) => setMapPoints(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/crm/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/crm/pickups").then((r) => setPickups(r.data)).catch(() => {});
    api.get("/crm/pickup-boys").then((r) => setBoys(r.data)).catch(() => {});
    loadRequests();
    loadMap();
    const t = setInterval(loadMap, 15000);
    return () => clearInterval(t);
  }, [loadMap, loadRequests]);

  return (
    <div data-testid="admin-crm" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">CRM & Pickup Monitor</h1>
        <p className="mt-1 text-sm text-slate-500">All enquiries, follow-up logs, pickup tasks and live pickup-boy locations.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className={`${card} p-5`} data-testid="crm-stat-requests">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">New pickup requests</p>
          <p className="mt-2 text-2xl font-bold text-sky-400">{requests.filter((r) => r.status === "new").length}</p>
        </div>
        <div className={`${card} p-5`} data-testid="crm-stat-boys">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pickup boys on duty</p>
          <p className="mt-2 text-2xl font-bold text-emerald-400">{boys.filter((b) => b.on_duty).length}<span className="text-sm text-slate-500"> / {boys.length}</span></p>
        </div>
        <div className={`${card} p-5`} data-testid="crm-stat-leads">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Enquiries</p>
          <p className="mt-2 text-2xl font-bold text-white">{leads.length}</p>
        </div>
        <div className={`${card} p-5`} data-testid="crm-stat-pickups">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Active pickups</p>
          <p className="mt-2 text-2xl font-bold text-saffron">{pickups.filter((p) => p.status !== "submitted_office").length}</p>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-bold text-white">Live Pickup Boy Map</h3>
        <PickupMap points={mapPoints} testid="admin-pickup-map" />
      </div>

      <div className={`${card} overflow-x-auto`}>
        <p className="border-b border-adminborder px-5 py-3.5 text-sm font-bold text-white">Pickup Tasks</p>
        <table className="w-full text-sm" data-testid="admin-pickups-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3">Customer</th><th className="px-5 py-3">Pickup Boy</th><th className="px-5 py-3">CRM</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3">AWB</th><th className="px-5 py-3">Notify</th><th className="px-5 py-3">Raised</th>
            </tr>
          </thead>
          <tbody>
            {pickups.map((t) => (
              <tr key={t.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`admin-pickup-row-${t.id}`}>
                <td className="px-5 py-3 font-bold text-white">{t.customer_name}<p className="text-[10px] font-normal text-slate-500">{t.phone} · {t.country}</p></td>
                <td className="px-5 py-3">{t.pickup_boy_name}</td>
                <td className="px-5 py-3 text-xs text-slate-400">{t.created_by_name}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${TASK_BADGE[t.status]}`}>{t.status?.replace("_", " ")}</span></td>
                <td className="px-5 py-3 font-mono text-xs">{t.shipment_awb || "—"}</td>
                <td className="px-5 py-3">
                  {t.notify?.wa_link && !t.notify?.sent_at && (
                    <button onClick={() => notifyCustomer(t)} data-testid={`admin-task-notify-${t.id}`} title="Send pickup confirmation on WhatsApp"
                            className="flex items-center gap-1 rounded-lg bg-emerald-500/15 px-2.5 py-1.5 text-[11px] font-bold text-emerald-400 hover:bg-emerald-500/25">
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </button>
                  )}
                  {t.notify?.sent_at && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400" data-testid={`admin-task-notified-${t.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Notified
                    </span>
                  )}
                  {!t.notify?.wa_link && !t.notify?.sent_at && <span className="text-xs text-slate-600">—</span>}
                </td>
                <td className="px-5 py-3 text-xs text-slate-500">{new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</td>
              </tr>
            ))}
            {!pickups.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No pickup tasks yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className={`${card} overflow-x-auto`}>
        <p className="border-b border-adminborder px-5 py-3.5 text-sm font-bold text-white">Website Pickup Requests</p>
        <table className="w-full min-w-[980px] text-sm" data-testid="admin-pickup-requests-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3">Request</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Address</th>
              <th className="px-5 py-3">Schedule</th><th className="px-5 py-3">Weight kg</th><th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Pickup Boy / Task</th><th className="px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`admin-req-row-${r.request_id}`}>
                <td className="px-5 py-3 font-mono text-xs font-bold text-saffron">{r.request_id}
                  <p className="text-[10px] font-normal text-slate-500">{new Date(r.created_at).toLocaleDateString("en-IN")}</p></td>
                <td className="px-5 py-3 font-bold text-white">{r.name}
                  <p className="text-[10px] font-normal text-slate-500">{r.phone}{r.email ? ` · ${r.email}` : ""}</p></td>
                <td className="max-w-[220px] px-5 py-3 text-xs text-slate-400">{r.address}{r.address2 ? `, ${r.address2}` : ""}, {r.city} {r.pincode}</td>
                <td className="px-5 py-3 text-xs text-slate-300">{r.pickup_date}<p className="text-[10px] text-slate-500">{r.time_slot}</p></td>
                <td className="px-5 py-3 text-xs text-slate-300">
                  {r.actual_weight ? `${r.actual_weight} act` : ""}{r.volumetric_weight ? ` / ${r.volumetric_weight} vol` : ""}
                  <p className="text-[10px] font-bold text-amber-400">{r.chargeable_weight ? `${r.chargeable_weight} chargeable` : "—"}</p></td>
                <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${REQ_BADGE[r.status] || REQ_BADGE.new}`} data-testid={`admin-req-status-${r.request_id}`}>{r.status}</span></td>
                <td className="px-5 py-3 text-xs text-slate-300">
                  {r.pickup_boy_name || "—"}
                  {r.task_status && <p className="text-[10px] text-slate-500">task: {r.task_status.replace("_", " ")}</p>}
                </td>
                <td className="px-5 py-3">
                  {r.status === "new" ? (
                    <div className="flex items-center gap-1.5">
                      <select className="rounded-lg border border-adminborder bg-adminbg px-2 py-1.5 text-xs text-slate-200 outline-none focus:border-saffron"
                              data-testid={`admin-req-boy-${r.request_id}`}
                              value={assignSel[r.id] || ""}
                              onChange={(e) => setAssignSel({ ...assignSel, [r.id]: e.target.value })}>
                        <option value="">Select boy…</option>
                        {boys.map((b) => <option key={b.id} value={b.id}>{b.name}{b.on_duty ? " ●" : ""}</option>)}
                      </select>
                      <button onClick={() => assignRequest(r)} disabled={busyId === r.id} data-testid={`admin-req-assign-${r.request_id}`}
                              className="rounded-lg bg-saffron px-3 py-1.5 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-60">Assign</button>
                      <button onClick={() => cancelRequest(r)} disabled={busyId === r.id} data-testid={`admin-req-cancel-${r.request_id}`}
                              className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/10 disabled:opacity-60">Cancel</button>
                    </div>
                  ) : <span className="text-xs text-slate-500">—</span>}
                </td>
              </tr>
            ))}
            {!requests.length && <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500" data-testid="admin-req-empty">No website pickup requests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className={`${card} overflow-x-auto`}>
        <p className="border-b border-adminborder px-5 py-3.5 text-sm font-bold text-white">CRM Enquiries & Follow-up Logs</p>
        <table className="w-full text-sm" data-testid="admin-leads-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3">Customer</th><th className="px-5 py-3">Country</th><th className="px-5 py-3">Quoted</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3">Follow-ups</th><th className="px-5 py-3">CRM</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`admin-lead-row-${l.id}`}>
                <td className="px-5 py-3 font-bold text-white">{l.name}<p className="text-[10px] font-normal text-slate-500">{l.phone}</p></td>
                <td className="px-5 py-3">{l.country} · {l.approx_kg}kg</td>
                <td className="px-5 py-3 text-emerald-400">{l.quoted_price ? `₹${Number(l.quoted_price).toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${LEAD_BADGE[l.status] || LEAD_BADGE.new}`}>{l.status}</span></td>
                <td className="px-5 py-3 text-xs">{l.followups?.length || 0}/3</td>
                <td className="px-5 py-3 text-xs text-slate-400">{l.created_by_name}</td>
                <td className="px-5 py-3">
                  {(l.followups || []).length > 0 && (
                    <button onClick={() => setOpenLead(openLead === l.id ? null : l.id)} data-testid={`admin-lead-logs-${l.id}`}
                            className="rounded-lg border border-adminborder px-2.5 py-1 text-[10px] font-bold text-slate-300 hover:border-saffron hover:text-saffron">
                      {openLead === l.id ? "Hide" : "Logs"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!leads.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No CRM enquiries yet.</td></tr>}
          </tbody>
        </table>
        {openLead && (() => {
          const l = leads.find((x) => x.id === openLead);
          return l ? (
            <div className="space-y-2 border-t border-adminborder p-5" data-testid="admin-lead-log-panel">
              <p className="text-xs font-bold text-white">Follow-up log — {l.name}</p>
              {l.followups.map((f, i) => (
                <div key={i} className="rounded-lg border border-adminborder p-3 text-xs">
                  <p className="font-bold text-slate-200">#{i + 1} · {f.by} · {new Date(f.at).toLocaleString("en-IN")}</p>
                  <p className="mt-1 text-slate-400">{f.note}{f.outcome ? ` — ${f.outcome}` : ""}</p>
                </div>
              ))}
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
}
